import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { Footprints, ArrowLeft, CheckCircle2 } from "lucide-react";

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    clinic_name: "",
    clinic_address: "",
    email: "",
    phone: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(formData);
      setSubmitted(true);
      toast.success("Registration submitted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <div className="p-6">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-[#0F766E] transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
        <div className="flex-1 flex items-center justify-center px-6 pb-12">
          <Card className="w-full max-w-md shadow-lg border-slate-200 text-center">
            <CardContent className="pt-8 pb-8">
              <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4 font-['Manrope']">
                Registration Submitted!
              </h2>
              <p className="text-slate-600 mb-6">
                Thank you for registering with Ortho Canada Lab. Our team will review your application and send you login credentials once approved.
              </p>
              <p className="text-sm text-slate-500 mb-6">
                This usually takes 24-48 business hours. You will receive an email with your username and temporary password.
              </p>
              <Link to="/login">
                <Button className="bg-[#0F766E] hover:bg-[#115E59]" data-testid="registration-success-login-btn">
                  Go to Login
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <div className="p-6">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-[#0F766E] transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>
      </div>

      {/* Registration Form */}
      <div className="flex-1 flex items-center justify-center px-6 pb-12">
        <Card className="w-full max-w-lg shadow-lg border-slate-200 overflow-hidden">
          {/* Red accent bar */}
          <div className="h-1 bg-gradient-to-r from-[#DC2626] to-[#0F766E]"></div>
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 w-12 h-12 rounded-lg bg-[#F0FDFA] flex items-center justify-center ring-2 ring-[#DC2626]/10">
              <Footprints className="w-6 h-6 text-[#0F766E]" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900 font-['Manrope']">
              Register Your Clinic
            </CardTitle>
            <CardDescription className="text-slate-600">
              Join our network of healthcare professionals
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    placeholder="John"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    data-testid="register-first-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    placeholder="Smith"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    data-testid="register-last-name-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="clinic_name">Clinic Name</Label>
                <Input
                  id="clinic_name"
                  name="clinic_name"
                  placeholder="Smith Podiatry Clinic"
                  value={formData.clinic_name}
                  onChange={handleChange}
                  required
                  data-testid="register-clinic-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="clinic_address">Clinic Address</Label>
                <Input
                  id="clinic_address"
                  name="clinic_address"
                  placeholder="123 Healthcare Ave, North York, ON"
                  value={formData.clinic_address}
                  onChange={handleChange}
                  required
                  data-testid="register-clinic-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john@smithclinic.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="register-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="(416) 555-0123"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  data-testid="register-phone-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-11 bg-[#0F766E] hover:bg-[#115E59] mt-2"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                    Submitting...
                  </span>
                ) : (
                  "Submit Registration"
                )}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <p className="text-slate-600">
                Already have an account?{" "}
                <Link to="/login" className="text-[#DC2626] font-medium hover:underline" data-testid="register-login-link">
                  Sign in
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
